import { useEffect } from "react";
import { productCache } from "@/services";
import { Product } from "@/components/business/products/ProductCard";

/**
 * Hook para sincronizar productos del backend con el productCache
 *
 * @description Pre-puebla el cache cuando se cargan productos desde el servidor,
 * eliminando la necesidad de requests individuales por producto
 *
 * @param backendProducts - Productos en formato backend
 * @param categoryById - Mapa de IDs de categoría a nombres
 * @param posId - ID del POS actual
 *
 * @example
 * ```tsx
 * const { data: posProducts = [] } = useProductsByPOS(posId);
 * useProductCacheSync(posProducts, categoryById, posId);
 * // Ahora todos los productos están en cache
 * ```
 */
export function useProductCacheSync(
  backendProducts: Array<{
    id: string;
    nombre: string;
    descripcion: string;
    precio: number | string;
    category_id: number;
    imagen_url?: string | null;
    esta_disponible: boolean;
  }>,
  categoryById: Record<number, string> = {},
  posId?: number | null
) {
  useEffect(() => {
    if (!backendProducts || backendProducts.length === 0) return;

    console.log(
      `🔄 Sincronizando ${backendProducts.length} productos al cache...`
    );

    let syncedCount = 0;

    backendProducts.forEach((backendProduct) => {
      // Mapear formato backend → frontend
      const frontendProduct: Product = {
        id: backendProduct.id,
        name: backendProduct.nombre,
        description: backendProduct.descripcion,
        price: Number(backendProduct.precio),
        category:
          categoryById[backendProduct.category_id] ??
          String(backendProduct.category_id),
        image: backendProduct.imagen_url ?? undefined,
        disponible: backendProduct.esta_disponible,
        stock: 0, // Valor por defecto
        rating: undefined, // Campo opcional
        posId: posId ?? undefined,
      };

      // Agregar al cache (solo si no existe o está expirado)
      productCache.setProduct(frontendProduct.id, frontendProduct);
      syncedCount++;
    });

    console.log(`✅ ${syncedCount} productos sincronizados al cache`);

    // Mostrar métricas del cache
    const metrics = productCache.getMetrics();
    console.log(
      `📊 Cache stats: ${metrics.totalCached} cached, Hit rate: ${metrics.hitRate.toFixed(1)}%`
    );
  }, [backendProducts, categoryById, posId]);
}
